/**
 * Fund Comparison Table Component
 *
 * Displays a side-by-side comparison of two funds (exit fund vs replacement fund)
 * with key metrics like returns, expense ratio, rank, and scores.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatPercent } from "@/lib/utils";
import {
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import type { FundRating } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund data for comparison
 */
export interface ComparisonFundData {
  /** Fund name */
  name: string;
  /** Fund rating */
  rating: FundRating;
  /** Rank in category */
  rank: number;
  /** Total funds in category */
  totalFundsInCategory: number;
  /** 1-year returns percentage */
  returns1Y: number;
  /** 3-year returns percentage */
  returns3Y: number;
  /** Expense ratio percentage */
  expenseRatio: number;
  /** Consistency score (0-100) */
  consistencyScore: number;
}

/**
 * Comparison metrics between two funds
 */
export interface ComparisonMetrics {
  /** Difference in 1Y returns (replacement - exit) */
  returnsDifference1Y: number;
  /** Difference in 3Y returns (replacement - exit) */
  returnsDifference3Y: number;
  /** Difference in expense ratio (exit - replacement, positive means savings) */
  expenseRatioDifference: number;
  /** Rank improvement (exit rank - replacement rank, positive means better) */
  rankImprovement: number;
  /** Consistency score difference (replacement - exit) */
  consistencyScoreDifference: number;
  /** Projected benefit description */
  projectedBenefit: string;
}

/**
 * Props for the FundComparisonTable component
 */
export interface FundComparisonTableProps {
  /** Exit fund data (current holding) */
  exitFund: ComparisonFundData;
  /** Replacement fund data (recommended) */
  replacementFund: ComparisonFundData;
  /** Pre-calculated comparison metrics */
  comparison?: ComparisonMetrics;
  /** Optional title override */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Rating configuration with colors
 */
const RATING_CONFIG: Record<
  FundRating,
  {
    label: string;
    bgColor: string;
    textColor: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    textColor: "text-emerald-700 dark:text-emerald-400",
  },
  "on-track": {
    label: "On-Track",
    bgColor: "bg-sky-100 dark:bg-sky-900/30",
    textColor: "text-sky-700 dark:text-sky-400",
  },
  "off-track": {
    label: "Off-Track",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    textColor: "text-amber-700 dark:text-amber-400",
  },
  "out-of-form": {
    label: "Out-of-Form",
    bgColor: "bg-rose-100 dark:bg-rose-900/30",
    textColor: "text-rose-700 dark:text-rose-400",
  },
};

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Comparison row showing metric for both funds
 */
function ComparisonRow({
  label,
  exitValue,
  replacementValue,
  formatValue,
  higherIsBetter = true,
  showDifference = true,
}: {
  label: string;
  exitValue: number;
  replacementValue: number;
  formatValue: (val: number) => string;
  higherIsBetter?: boolean;
  showDifference?: boolean;
}) {
  const difference = replacementValue - exitValue;
  const isImprovement = higherIsBetter ? difference > 0 : difference < 0;
  const isDifferent = Math.abs(difference) > 0.001;

  return (
    <div className="grid grid-cols-3 gap-2 py-2.5 items-center">
      {/* Exit Fund Value */}
      <div className="text-center">
        <span className="font-mono text-sm">{formatValue(exitValue)}</span>
      </div>

      {/* Label with difference indicator */}
      <div className="text-center px-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        {showDifference && isDifferent && (
          <div
            className={cn(
              "flex items-center justify-center gap-0.5 text-xs mt-0.5",
              isImprovement
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400"
            )}
          >
            {isImprovement ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            <span>{formatValue(Math.abs(difference))}</span>
          </div>
        )}
      </div>

      {/* Replacement Fund Value */}
      <div className="text-center">
        <span
          className={cn(
            "font-mono text-sm",
            isDifferent && isImprovement && "text-emerald-600 dark:text-emerald-400 font-medium"
          )}
        >
          {formatValue(replacementValue)}
        </span>
      </div>
    </div>
  );
}

/**
 * Rating badge row
 */
function RatingRow({
  exitRating,
  replacementRating,
}: {
  exitRating: FundRating;
  replacementRating: FundRating;
}) {
  const exitConfig = RATING_CONFIG[exitRating];
  const replacementConfig = RATING_CONFIG[replacementRating];

  return (
    <div className="grid grid-cols-3 gap-2 py-2.5 items-center">
      {/* Exit Fund Rating */}
      <div className="flex justify-center">
        <Badge
          className={cn(
            "text-xs",
            exitConfig.bgColor,
            exitConfig.textColor
          )}
        >
          {exitConfig.label}
        </Badge>
      </div>

      {/* Label */}
      <div className="text-center">
        <span className="text-xs text-muted-foreground">Rating</span>
      </div>

      {/* Replacement Fund Rating */}
      <div className="flex justify-center">
        <Badge
          className={cn(
            "text-xs",
            replacementConfig.bgColor,
            replacementConfig.textColor
          )}
        >
          {replacementConfig.label}
        </Badge>
      </div>
    </div>
  );
}

/**
 * Rank row with special formatting
 */
function RankRow({
  exitRank,
  exitTotal,
  replacementRank,
  replacementTotal,
}: {
  exitRank: number;
  exitTotal: number;
  replacementRank: number;
  replacementTotal: number;
}) {
  const improvement = exitRank - replacementRank;
  const isImprovement = improvement > 0;

  return (
    <div className="grid grid-cols-3 gap-2 py-2.5 items-center">
      {/* Exit Fund Rank */}
      <div className="text-center">
        <span className="font-mono text-sm">
          #{exitRank}
          <span className="text-muted-foreground text-xs">/{exitTotal}</span>
        </span>
      </div>

      {/* Label with improvement */}
      <div className="text-center px-2">
        <span className="text-xs text-muted-foreground">Category Rank</span>
        {improvement !== 0 && (
          <div
            className={cn(
              "flex items-center justify-center gap-0.5 text-xs mt-0.5",
              isImprovement
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400"
            )}
          >
            {isImprovement ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            <span>{Math.abs(improvement)} places</span>
          </div>
        )}
      </div>

      {/* Replacement Fund Rank */}
      <div className="text-center">
        <span
          className={cn(
            "font-mono text-sm",
            isImprovement && "text-emerald-600 dark:text-emerald-400 font-medium"
          )}
        >
          #{replacementRank}
          <span className="text-muted-foreground text-xs">/{replacementTotal}</span>
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Fund Comparison Table
 *
 * Displays a side-by-side comparison of exit fund vs replacement fund
 * with visual indicators showing improvements in each metric.
 *
 * @example
 * ```tsx
 * <FundComparisonTable
 *   exitFund={{
 *     name: "Nippon India Growth Fund",
 *     rating: "off-track",
 *     rank: 15,
 *     totalFundsInCategory: 28,
 *     returns1Y: 22.34,
 *     returns3Y: 18.67,
 *     expenseRatio: 0.85,
 *     consistencyScore: 62,
 *   }}
 *   replacementFund={{
 *     name: "Kotak Emerging Equity Fund",
 *     rating: "in-form",
 *     rank: 2,
 *     totalFundsInCategory: 28,
 *     returns1Y: 34.12,
 *     returns3Y: 26.78,
 *     expenseRatio: 0.45,
 *     consistencyScore: 86,
 *   }}
 * />
 * ```
 */
export function FundComparisonTable({
  exitFund,
  replacementFund,
  comparison,
  title = "Side-by-Side Comparison",
  className,
}: FundComparisonTableProps) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-2 pb-3 border-b border-border/50 mb-2">
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Current
            </p>
            <p className="text-sm font-medium truncate mt-0.5" title={exitFund.name}>
              {exitFund.name.split(" ").slice(0, 2).join(" ")}
            </p>
          </div>
          <div className="flex items-center justify-center">
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Recommended
            </p>
            <p className="text-sm font-medium truncate mt-0.5" title={replacementFund.name}>
              {replacementFund.name.split(" ").slice(0, 2).join(" ")}
            </p>
          </div>
        </div>

        {/* Comparison Rows */}
        <div className="divide-y divide-border/30">
          {/* Rating */}
          <RatingRow
            exitRating={exitFund.rating}
            replacementRating={replacementFund.rating}
          />

          {/* Rank */}
          <RankRow
            exitRank={exitFund.rank}
            exitTotal={exitFund.totalFundsInCategory}
            replacementRank={replacementFund.rank}
            replacementTotal={replacementFund.totalFundsInCategory}
          />

          {/* 1Y Returns */}
          <ComparisonRow
            label="1Y Returns"
            exitValue={exitFund.returns1Y}
            replacementValue={replacementFund.returns1Y}
            formatValue={(val) => formatPercent(val, 1)}
            higherIsBetter={true}
          />

          {/* 3Y Returns */}
          <ComparisonRow
            label="3Y Returns"
            exitValue={exitFund.returns3Y}
            replacementValue={replacementFund.returns3Y}
            formatValue={(val) => formatPercent(val, 1)}
            higherIsBetter={true}
          />

          {/* Expense Ratio */}
          <ComparisonRow
            label="Expense Ratio"
            exitValue={exitFund.expenseRatio}
            replacementValue={replacementFund.expenseRatio}
            formatValue={(val) => `${val.toFixed(2)}%`}
            higherIsBetter={false}
          />

          {/* Consistency Score */}
          <ComparisonRow
            label="Consistency"
            exitValue={exitFund.consistencyScore}
            replacementValue={replacementFund.consistencyScore}
            formatValue={(val) => `${Math.round(val)}/100`}
            higherIsBetter={true}
          />
        </div>

        {/* Projected Benefit */}
        {comparison?.projectedBenefit && (
          <div className="mt-4 pt-3 border-t border-border/50">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {comparison.projectedBenefit}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FundComparisonTable
 */
export function FundComparisonTableSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-40" />
      </CardHeader>
      <CardContent>
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-2 pb-3 border-b border-border/50 mb-2">
          <div className="text-center space-y-1">
            <Skeleton className="h-3 w-12 mx-auto" />
            <Skeleton className="h-4 w-20 mx-auto" />
          </div>
          <div className="flex items-center justify-center">
            <Skeleton className="h-4 w-4" />
          </div>
          <div className="text-center space-y-1">
            <Skeleton className="h-3 w-16 mx-auto" />
            <Skeleton className="h-4 w-20 mx-auto" />
          </div>
        </div>

        {/* Comparison Rows */}
        <div className="space-y-3">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="grid grid-cols-3 gap-2 py-2 items-center">
              <div className="flex justify-center">
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-3 w-10" />
              </div>
              <div className="flex justify-center">
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          ))}
        </div>

        {/* Projected Benefit */}
        <div className="mt-4 pt-3 border-t border-border/50">
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      </CardContent>
    </Card>
  );
}

export default FundComparisonTable;
